/** @format */

import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	PLATFORM_ID,
	SimpleChanges,
	ViewChild,
	output
} from '@angular/core';
import { AsyncPipe, isPlatformBrowser, NgStyle, SlicePipe } from '@angular/common';
import {
	BRAND_FILTER_ID,
	CATEGORY_FILTER_ID,
	GTGT_FILTER_NAME,
	GTGT_FILTER_VALUE_DSG_PL,
	GTGT_FILTER_VALUE_GG,
	GTGT_TITLE,
	KIDS_SHOES_SIZE,
	MENS_SHOES_SIZE,
	PRICE_FILTER_ID,
	RATINGS_FILTER_ID,
	SIZE_FILTER_ID,
	WOMENS_SHOES_SIZE
} from '@plp/data-access/constants';
import {
	CategoryDetails,
	FacetAccordionElement,
	FacetVO,
	FacetVOValue,
	RefinementUpdateObj
} from '@plp/data-access/interfaces';
import { FilterByPipe, SortByPipe } from '@plp/utils';
import { HmfActionTextComponent, HmfTextInputComponent } from '@dsg-tech/homefield-angular';
import {
	HomefieldAccordionModule,
	HomefieldCheckboxModule,
	HomefieldTextLinkModule
} from '@homefield';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconSearch
} from '@dsg-tech/homefield-angular-icons';
import { Subject, combineLatest } from 'rxjs';
import {
	refreshProductList,
	selectProductListCategoryDetails,
	selectProductListSEO,
	selectSelectedFilters,
	setFiltering,
	setRefinementUpdateObj
} from '@plp/data-access/state';

import { Chain } from '@shared/data-access/interfaces';
import { FormsModule } from '@angular/forms';
import { ProductListUIService } from '@plp/data-access/services';
import { Store } from '@ngrx/store';
import { UtilityService } from '@shared/data-access/services';
import { takeUntil } from 'rxjs/operators';
import { FilterOptionComponent } from './filter-option/filter-option.component';

@Component({
	selector: 'plp-components-filter',
	imports: [
		AsyncPipe,
		SlicePipe,
		SortByPipe,
		FilterByPipe,
		FilterOptionComponent,
		FormsModule,
		HmfTextInputComponent,
		HmfActionTextComponent,
		HomefieldAccordionModule,
		HomefieldCheckboxModule,
		HomefieldIconLibraryModule,
		HomefieldTextLinkModule,
		NgStyle
	],
	templateUrl: './filter.component.html',
	styleUrl: './filter.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
	@ViewChild('scrollableParent', { static: false }) scrollableParent: ElementRef;

	@Input() filter: FacetVO;
	@Input() disabled = false;
	@Input() defaultOpen = false;
	@Input() showMore = false;
	@Input() isMobile = false;
	@Input() isLast = false;
	@Input() isPlp3Template = false;
	@Output() facetToggled = new EventEmitter<FacetAccordionElement>();
	accordionFocused = output<void>();
	pressedSpaceOnCategoryItem = output<void>();

	private readonly UNSUBSCRIBE$ = new Subject<void>();

	readonly productListSEOData$ = this.store
		.select(selectProductListSEO)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly selectedFilters$ = this.store
		.select(selectSelectedFilters)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly productListCategoryDetails$ = this.store
		.select(selectProductListCategoryDetails)
		.pipe(takeUntil(this.UNSUBSCRIBE$));

	GTGT_TITLE = GTGT_TITLE;
	GTGT_FILTER_NAME = GTGT_FILTER_NAME;
	selectedOptions: string[] = [];
	selectedSubheaders = '';
	displayText: { [key: string]: string | string[] } = {};
	minPrice: number | null = null;
	maxPrice: number | null = null;
	errorMessage = null;
	filterHeight = '';
	brandFilterString = '';
	regexForLettersOnly = /[A-za-z]/;
	categoryDetails: CategoryDetails | null = null;
	isPriceFilter = false;
	isCategoryFilter = false;
	isBrandFilter = false;
	isRatingsFilter = false;
	isSizeFilter = false;
	width = '100%';
	filteredList: { count: number; value: string }[] = [];
	selectedFilters: string[] = [];

	/**
	 * @description
	 * boolean for if the number of options does not fit in the default height of the container.
	 */
	hasOverflow = false;
	currentFocusedElement: HTMLElement | null = null;

	constructor(
		private readonly store: Store,
		private readonly productListUIService: ProductListUIService,
		private readonly utilityService: UtilityService,
		private readonly chain: Chain,
		private readonly homefieldIconsLibraryService: HomefieldIconsLibraryService,
		@Inject(PLATFORM_ID) private readonly platformId: string
	) {
		this.homefieldIconsLibraryService.registerIcons([homefieldIconSearch]);
	}

	ngOnInit(): void {
		combineLatest({
			filters: this.selectedFilters$,
			productListCategoryDetails: this.productListCategoryDetails$
		})
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe((res) => {
				if (res.productListCategoryDetails) {
					this.categoryDetails = res.productListCategoryDetails;
				}

				if (res.filters[this.filter.attrIdentifier]) {
					this.selectedOptions = [...res.filters[this.filter.attrIdentifier]];
					if (this.isPriceFilter) {
						this.setMinAndMaxPriceValuesFromState();
					}
				} else {
					this.selectedOptions = [];
				}

				this.setSubheaders();
				this.initializeDefaultFilterHeights();
				this.setFilteredList();
			});
	}

	ngAfterViewInit(): void {
		this.scrollToFilter();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes?.filter?.currentValue) {
			// Force the update because Angular doesn't recognize deep object changes
			this.filter = { ...changes.filter.currentValue };
			this.initializeDefaultFilterHeights();
			this.setFilteredList();
		}

		// logic to mantain focus after a filter is selected using the keyboard
		if (isPlatformBrowser(this.platformId) && !changes?.disabled?.currentValue) {
			if (this.currentFocusedElement) {
				setTimeout(() => {
					this.currentFocusedElement?.focus({ preventScroll: true });
					this.currentFocusedElement = null;
				}, 100);
			} else if (document.activeElement?.classList.contains('filter-value-count-container')) {
				this.pressedSpaceOnCategoryItem.emit();
			}
		}

		//Set up checks for specific filter conditions
		this.isPriceFilter = this.filter.attrIdentifier === PRICE_FILTER_ID;
		this.isCategoryFilter = this.filter.attrIdentifier === CATEGORY_FILTER_ID;
		this.isBrandFilter = this.filter.attrIdentifier === BRAND_FILTER_ID;
		this.isRatingsFilter = this.filter.attrIdentifier === RATINGS_FILTER_ID;
		this.isSizeFilter =
			this.filter.attrIdentifier === SIZE_FILTER_ID ||
			this.filter.attrIdentifier === KIDS_SHOES_SIZE ||
			this.filter.attrIdentifier === WOMENS_SHOES_SIZE ||
			this.filter.attrIdentifier === MENS_SHOES_SIZE;

		//For the price filter, map the raw data (ex. *-25) to the formatted text to display (ex. under $25)
		if (this.isPriceFilter) {
			this.filter.valueList.forEach((filterOption: FacetVOValue) => {
				this.displayText[filterOption.value] =
					this.productListUIService.addSymbolForPriceDisplay(filterOption.value);
			});
		}

		//For Guaranteed To Get There, map the raw data (ex. Y) to the text we want to display (ex. Get It By 12/24)
		if (this.filter.attrName === GTGT_FILTER_NAME) {
			this.displayText['Y'] =
				this.chain.chainIdentifierAbbr === 'gg'
					? [GTGT_FILTER_VALUE_GG]
					: [GTGT_FILTER_VALUE_DSG_PL];
		}
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	setFilteredList(): void {
		// If view more was selected, show the whole filter list
		// otherwise show the top 10 for size filter and 5 for others.
		if (this.showMore) {
			this.filteredList = this.filter?.valueList;
		} else {
			this.filteredList = this.filter?.valueList?.slice(0, this.isSizeFilter ? 10 : 5);
		}
	}

	brandInputChanged(): void {
		this.showMore = true;
		this.facetToggled.emit({
			attrName: this.filter.attrName,
			open: true,
			showMore: this.showMore
		});
	}

	initializeDefaultFilterHeights(): void {
		if (this.isBrandFilter || this.isSizeFilter) {
			this.filterHeight = '352px';
			this.hasOverflow = this.filter.valueList.length > 10;
		} else {
			this.filterHeight = '135px';
			this.hasOverflow = this.filter.valueList.length > 5;
		}
	}

	// this happens when you click show more or view less
	toggleShowMore(): void {
		this.showMore = !this.showMore;
		this.facetToggled.emit({
			attrName: this.filter.attrName,
			open: true,
			showMore: this.showMore
		});
		this.setFilteredList();
	}

	async setSelectedFilter(filterName: string): Promise<void> {
		if (this.disabled) {
			return;
		}
		//If the clicked on option is already selected, remove it from the array
		if (this.selectedOptions.includes(filterName)) {
			this.selectedOptions = this.selectedOptions.filter((e) => e !== filterName);
		} else {
			if (this.isCategoryFilter) {
				// For single select filters, replace array with new array containing only the selected filter
				this.selectedOptions = [filterName];
			} else {
				//If the filter is a multi select filter, add the new filter to the array
				this.selectedOptions.push(filterName);
			}

			this.setAnalyticsInfo(filterName);
		}
		this.currentFocusedElement = document.activeElement as HTMLElement;
		const plpContainerElem = document.getElementById('plp-list-container');
		this.utilityService.scrollToElement(
			plpContainerElem,
			{
				behavior: 'instant',
				block: 'start',
				inline: 'nearest'
			},
			0
		);

		// Validate if the browser supports Scheduler.yield fn
		this.utilityService.yieldTask(() => this.dispatchActions());
	}

	setSelectedFilterWithSpace(event: KeyboardEvent, filterName: string): void {
		event.preventDefault();

		this.setSelectedFilter(filterName);
	}

	dispatchActions(): void {
		const filterType: string = this.filter.attrIdentifier;
		this.store.dispatch(
			setFiltering({
				selectedFilters: {
					[filterType]: this.selectedOptions
				}
			})
		);
		this.store.dispatch(refreshProductList());
	}

	/**
	 * @description Function is used to load previous price filtering values from the state store if available.
	 */
	setMinAndMaxPriceValuesFromState(): void {
		let boxSelected = false;
		//Check if the selected price options is one of the pre-created ones from the api
		this.filter.valueList.forEach((filterOption: FacetVOValue) => {
			if (this.selectedOptions.includes(filterOption.value)) {
				boxSelected = true;
			}
		});

		//If the selected option is not a pre-created value, parse options and set the min and max price for use in the price text input feilds
		if (!boxSelected && this.selectedOptions[0]) {
			const valArray = this.selectedOptions[0].split('-');
			this.minPrice = parseInt(valArray[0]);
			this.maxPrice = parseInt(valArray[1]);
		}
	}

	setAnalyticsInfo(filterName: string): void {
		const refinementUpdateObj: RefinementUpdateObj = {
			UpdateType: 'Refinement',
			UpdateValue: {
				RefinementValue: filterName,
				RefinementAttribute: this.filter.attrName,
				ProductCountTotal: undefined
			}
		};
		this.store.dispatch(setRefinementUpdateObj({ refinementUpdateObj }));
	}

	/**
	 * @description Creates unique identifiers for the DOM to use to track filters so that it does not have to completely re-render every time the data changes.
	 */
	trackFilterName(index: number, filterOption: FacetVOValue): string {
		return index.toString() + filterOption.value;
	}

	// this is when you open or close the accordion
	toggleFacet(open: boolean) {
		this.facetToggled.emit({
			attrName: this.filter.attrName,
			open,
			showMore: false
		});
	}

	setSubheaders() {
		let subheaders: string[] = [];
		if (!this.selectedOptions.length) {
			this.selectedSubheaders = '';
			return;
		}
		if (this.isCategoryFilter && this.categoryDetails) {
			subheaders = this.selectedOptions.map((element) => this.categoryDetails[element]?.name);
		} else if (this.filter?.attrName === GTGT_FILTER_NAME) {
			this.displayText['Y'] =
				this.chain.chainIdentifierAbbr === 'gg'
					? [GTGT_FILTER_VALUE_GG]
					: [GTGT_FILTER_VALUE_DSG_PL];
			// format the subheaders for the price filter, i.e. *-25 -> Under $25, 500-* -> $500 & Above
		} else if (this.isPriceFilter) {
			subheaders = this.selectedOptions.map((option) =>
				this.productListUIService.addSymbolForPriceDisplay(option)
			);
		} else {
			subheaders = [...this.selectedOptions];
		}
		this.selectedSubheaders = subheaders.join(', ');
	}

	scrollToFilter(): void {
		if (!this.selectedOptions.length) {
			return;
		}

		const mostRecentlySelected = this.selectedOptions.length - 1;
		const filterItem: string = this.selectedOptions[mostRecentlySelected];
		const filterItemId = this.isMobile ? `filter-mobile-${filterItem}` : `filter-${filterItem}`;
		const selectedFilter = document.getElementById(filterItemId);

		if (selectedFilter !== null) {
			this.scrollableParent.nativeElement.scroll({
				top: selectedFilter.offsetTop,
				behavior: 'smooth'
			});
		}
	}

	onAccordionFocus(): void {
		this.accordionFocused.emit();
	}
}
